import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { CorrelationData } from '../../types';

interface CorrelationMatrixHeatmapProps {
  data: CorrelationData[];
}

export const CorrelationMatrixHeatmap: React.FC<CorrelationMatrixHeatmapProps> = ({ data }) => {
  const variables = Array.from(
    new Set(data.flatMap(d => [d.variable1, d.variable2]))
  );

  const getCorrelation = (var1: string, var2: string): number | null => {
    if (var1 === var2) return 1;

    const correlation = data.find(
      d => (d.variable1 === var1 && d.variable2 === var2) || (d.variable1 === var2 && d.variable2 === var1)
    );

    return correlation ? correlation.correlation : null;
  };

  const getColorForCorrelation = (correlation: number | null): string => {
    if (correlation === null) return 'bg-gray-800';
    if (correlation === 1) return 'bg-blue-600';

    const abs = Math.abs(correlation);

    if (correlation > 0) {
      if (abs > 0.7) return 'bg-green-700';
      if (abs > 0.5) return 'bg-green-600';
      if (abs > 0.3) return 'bg-green-500/70';
      return 'bg-green-500/30';
    } else {
      if (abs > 0.7) return 'bg-red-700';
      if (abs > 0.5) return 'bg-red-600';
      if (abs > 0.3) return 'bg-red-500/70';
      return 'bg-red-500/30';
    }
  };

  const getTextColorForCorrelation = (correlation: number | null): string => {
    if (correlation === null) return 'text-gray-500';
    if (correlation === 1) return 'text-white';

    const abs = Math.abs(correlation);
    if (abs > 0.5) return 'text-white';
    return 'text-gray-200';
  };

  const sortedCorrelations = [...data].sort((a, b) => Math.abs(b.correlation) - Math.abs(a.correlation));
  const topCorrelations = sortedCorrelations.slice(0, 5);

  return (
    <div className="space-y-6">
      <div className="premium-chart-card">
        <div className="mb-6">
          <h4 className="premium-chart-title">Matrice di Correlazione</h4>
          <p className="premium-chart-subtitle">Correlazioni di Pearson tra le variabili principali</p>
        </div>

        <div className="overflow-x-auto">
          <div className="inline-block min-w-full">
            <div className="grid" style={{ gridTemplateColumns: `120px repeat(${variables.length}, minmax(80px, 1fr))` }}>
              <div className="p-2"></div>
              {variables.map((variable, index) => (
                <div key={index} className="p-2 text-center">
                  <span className="text-xs font-medium text-gray-300 transform -rotate-45 inline-block whitespace-nowrap">
                    {variable}
                  </span>
                </div>
              ))}

              {variables.map((var1, yIndex) => (
                <React.Fragment key={yIndex}>
                  <div className="p-2 flex items-center">
                    <span className="text-xs font-medium text-gray-300">{var1}</span>
                  </div>
                  {variables.map((var2, xIndex) => {
                    const correlation = getCorrelation(var1, var2);
                    const colorClass = getColorForCorrelation(correlation);
                    const textColorClass = getTextColorForCorrelation(correlation);

                    return (
                      <div
                        key={xIndex}
                        className={`p-3 border border-gray-700 transition-all hover:scale-105 hover:z-10 hover:shadow-lg cursor-pointer ${colorClass}`}
                        title={`${var1} vs ${var2}\nCorrelazione: ${correlation !== null ? correlation.toFixed(3) : 'N/A'}`}
                      >
                        <div className="text-center">
                          <p className={`text-xs font-bold ${textColorClass}`}>
                            {correlation !== null ? correlation.toFixed(2) : '-'}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">Legenda Correlazione</span>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-700 rounded"></div>
                <span className="text-xs text-gray-400">Positiva Forte (&gt;0.7)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-500/70 rounded"></div>
                <span className="text-xs text-gray-400">Positiva Media (0.3-0.7)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-red-500/70 rounded"></div>
                <span className="text-xs text-gray-400">Negativa Media (-0.3 to -0.7)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-red-700 rounded"></div>
                <span className="text-xs text-gray-400">Negativa Forte (&lt;-0.7)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="premium-chart-card">
        <h4 className="premium-chart-title mb-4">Top 5 Correlazioni Più Forti</h4>
        <div className="space-y-3">
          {topCorrelations.map((corr, index) => {
            const isPositive = corr.correlation > 0;
            const absCorr = Math.abs(corr.correlation);

            return (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800/70 transition-colors">
                <div className="flex items-center space-x-3 flex-1">
                  <div className={`p-2 rounded-lg ${
                    isPositive ? 'bg-green-500/20' : 'bg-red-500/20'
                  }`}>
                    {isPositive ? (
                      <TrendingUp className="h-4 w-4 text-green-400" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-red-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">
                      {corr.variable1} <span className="text-gray-500">vs</span> {corr.variable2}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {absCorr > 0.7 ? 'Correlazione molto forte' : absCorr > 0.5 ? 'Correlazione forte' : 'Correlazione moderata'}
                    </p>
                  </div>
                </div>

                <div className="text-right">
                  <p className={`text-lg font-bold ${
                    isPositive ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {corr.correlation.toFixed(3)}
                  </p>
                  <div className="flex items-center justify-end space-x-1 mt-1">
                    <div className="w-16 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          isPositive ? 'bg-green-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${absCorr * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-400">{(absCorr * 100).toFixed(0)}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
